import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

const pillars: Pillar[] = [
  {
    title: 'Kapalı ağda, internete çıkmadan',
    description:
      'Monosign ve Monopam internete kapalı sahalarda, ayrık ağ bölgelerinde ve fiziksel olarak izole ortamlarda çalışacak şekilde kurulur; istisna olarak değil, desteklenen kurulum biçimi olarak.',
    bullets: [
      'Air-gap dostu kurulum ve çevrimdışı işletim',
      'Güncellemeler dahil dışa bağımlılıksız yaşam döngüsü',
      'Ayrık ağ bölgeleri için bölge başına gateway yerleşimi',
      'Korunan ağa içeri doğru bağlantı açılmaz',
    ],
  },
  {
    title: 'Rehber ve denetim beklentileriyle hizalı',
    description:
      'Merkezi kimlik doğrulama, ayrıcalıklı erişim kontrolü, iz kaydı ve görevler ayrılığı: kamu bilgi güvenliği rehberlerinin kimlik ve erişim başlıklarına karşılık gelen kontroller ürünün içinde.',
    bullets: [
      'Merkezi kimlik doğrulama ve yetkilendirme',
      'Çok faktörlü doğrulama, kimlik avına dirençli faktörlerle',
      'Erişim ve yetki değişikliklerinde onay akışları',
      'Denetime hazır, dışa aktarılabilir kanıt raporları',
    ],
  },
  {
    title: 'Ayrıcalıklı erişim kayıt altında',
    description:
      'Sunuculara, veritabanlarına ve ağ cihazlarına erişim kasadan geçer; her ayrıcalıklı oturum video ve dökümüyle kaydedilir.',
    bullets: [
      'Kimlik bilgileri kasada, kullanıcı parolayı hiç görmez',
      'Video + döküm ile tam oturum kaydı',
      'Komut filtreleme ve oturum içi politika',
      'Süre sınırlı, onaya bağlı yetki yükseltme',
    ],
  },
  {
    title: 'Uzun süreli iz kaydı ve raporlama',
    description:
      'Denetim izleri regülasyonların gerektirdiği süreler boyunca, kurcalamaya dayanıklı biçimde saklanır; denetim günü geldiğinde kanıt üretmek rapor almak kadar kolaydır.',
    bullets: [
      'Her kimlik olayı için değiştirilemez iz kaydı',
      'Uzun süreli saklama ve arşivleme',
      'SIEM entegrasyonu ile merkezi izleme',
      'Zamanlanmış denetim raporları',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Yerinde kurun',
    description:
      'Kapalı ağda veya kendi veri merkezinizde, dakikalar içinde ayağa kaldırın, dış bağımlılık olmadan.',
  },
  {
    title: 'Erişimi merkezileştirin',
    description:
      'Dizinleri bağlayın, uygulamaları tek kimliğe alın, ayrıcalıklı hesapları kasaya taşıyın.',
  },
  {
    title: 'Denetime hazır olun',
    description:
      'Oturum kayıtları, iz kayıtları ve zamanlanmış raporlarla kanıt her an elinizin altında.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Yerli kimlik yönetimi',
    description: 'Veri Türkiye’de kalır; mimariyle, sözleşmeyle değil.',
    href: '/solutions/yerli-kimlik-yonetimi',
  },
  {
    label: 'Kamu sektörü',
    description: 'Kamu ve kritik altyapı için sektör sayfası.',
    href: '/solutions/industries/public-sector',
  },
  {
    label: 'KVKK uyumu',
    description: 'Log ve raporlarla KVKK yükümlülüklerine destek.',
    href: '/compliance/kvkk',
  },
]

export default function KamuKimlikYonetimiPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Kamu için kimlik ve erişim yönetimi"
        description="Kamu kurumları ve kritik altyapı için kimlik yönetimi: kapalı ağ ve air-gap kurulum, merkezi kimlik doğrulama, ayrıcalıklı erişim kaydı ve uzun süreli denetim izi, tamamen kendi bünyenizde."
        path="/tr/solutions/kamu-kimlik-yonetimi"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Kullanım senaryoları · Kamu"
          title={
            <>
              Kamu için kimlik yönetimi.
              <br />
              Kapalı ağda, kendi bünyenizde.
            </>
          }
          body="Kamu kurumlarının kimlik altyapısı buluta çıkamayabilir; çıkmak zorunda da değil. Monosign ve Monopam internete kapalı ağlarda uçtan uca çalışır: merkezi kimlik doğrulama, ayrıcalıklı erişim kaydı ve denetime hazır iz kayıtları; tamamı kendi altyapınızda."
          primaryCta={{ label: 'Ücretsiz deneme başlat →', href: '/try/now' }}
          secondaryCta={{ label: 'Uzmanlarımızla görüşün', href: '/contact' }}
          badges={[
            'Air-gap ve kapalı ağ kurulumu',
            'Ayrıcalıklı oturum kaydı',
            'Uzun süreli denetim izi',
          ]}
        />
        <SolutionPillars
          eyebrow="Ne elde edersiniz"
          title={<>Denetime hazır kimlik altyapısı, dışa bağımlılıksız.</>}
          body="Kimlik verisi kurumun sınırları içinde kalır; kontroller rehber ve denetim beklentileriyle hizalı çalışır; kanıt üretmek arkeoloji değil, rapor almaktır."
          pillars={pillars}
        />
        <ProductHowItWorks
          eyebrow="Nasıl çalışır"
          title={<>Yerinde kurun. Merkezileştirin. Kanıtlayın.</>}
          steps={steps}
        />
        <SolutionRelated
          eyebrow="Keşfetmeye devam edin"
          title="Bu senaryonun arkasındaki yetenekler."
          items={related}
        />
        <AssetDownload
          asset="kvkk-rehber"
          title={{
            en: 'KVKK için kimlik ve erişim rehberi.',
            tr: 'KVKK için kimlik ve erişim rehberi.',
          }}
          copy={{
            en: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: envanterden değiştirilemez loglara. (Türkçe PDF)',
            tr: 'Kişisel veriye erişimi kanıtlanabilir biçimde yönetmek için beş bölümlük kontrol listesi: erişim envanterinden değiştirilemez loglara.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

// TR-only: kamu anlatısının uluslararası karşılığı yok; EN locale 404 döner.
export async function getStaticProps({ locale }: { locale: string }) {
  if (locale !== 'tr') {
    return { notFound: true }
  }
  return { props: {} }
}
